﻿import { ParameterSchemaStation, ParameterSchemaSensor } from "../../API/Schema/ParameterSchema";
import { DefaultGroupFormatter } from "../../Formatters/DefaultGroupFormatter";
import { ILiveDataListenerContext, ILiveDataListener } from "../../ILiveDataListener";
import { MainSite } from "../../MainSite";
import { WarningGroup } from "../../Model/WarningGroup";
import { ParameterGroupInfo, ParameterGroupInfoSet } from "../../ParameterGroupInfoSet";
import { DirectionalPolarChart } from "./DirectionalPolarChart";
import { ISiteObservationsPageWindTab } from "./ISiteObservationsPage";
import { ValueBox } from "./ValueBox";
import { WindChart } from "./WindChart";
import { WindCompass } from "./WindCompass";

export class Wind implements ISiteObservationsPageWindTab {

    public id: string = 'wind';
    public label: string = 'Wind';

    public station: ParameterSchemaStation;
    public warningGroup: WarningGroup;

    private windCompasses: WindCompass[]; 
    private selectedHours: number;
    private polarCharts: DirectionalPolarChart[] = [];

    private validSensorIds: string[];

    private isValidSensorId(sensorId: string, station: ParameterSchemaStation) {

        // Leave out forecasts
        if (sensorId.includes("orecast")) {
            return false;
        }
        
        const sensor = station.Sensors[sensorId];

        if (!sensor.UseLiveData) {
            return false;
        }

        for (let paramId in sensor.Parameters) {

            // if either a WindSpeed or WindDirection parameter is present, we consider it to be a wind sensor
            if (paramId.indexOf("WindSpeed") > -1 || paramId.indexOf("WindDirection") > -1) {

                // Check if the non-10m version is valid
                if (paramId.indexOf('Wind') != -1 && paramId.indexOf('_10m_') == -1 && this.is10m != true) {
                    return true;
                }

                // Check if the 10m version is valid
                if (paramId.indexOf('Wind') != -1 && paramId.indexOf('_10m_') != -1 && this.is10m == true) {
                    return true;
                }
            }
        }

        return false;
    }

    public show(station: ParameterSchemaStation): boolean {

        this.validSensorIds = [];

        // Show this page if: station has any windSensors (which are not forecasts)
        for (let sensorId in station.Sensors) {

            if (this.isValidSensorId(sensorId, station)) {
                this.validSensorIds.push(sensorId)
            }

        }

        if (this.validSensorIds.length > 0) {
            return true;
        } else {
            return false;
        }
    }

    public windSpeedGroup: ParameterGroupInfo = ParameterGroupInfoSet.WindSpeed_kts;
    public gustSpeedGroup: ParameterGroupInfo = ParameterGroupInfoSet.GustSpeed_kts;
    public windSpeedGroup1min: ParameterGroupInfo = ParameterGroupInfoSet.WindSpeed_kts_1min;
    public gustSpeedGroup1min: ParameterGroupInfo = ParameterGroupInfoSet.GustSpeed_kts_1min;
    public windSpeedGroup10min: ParameterGroupInfo = ParameterGroupInfoSet.WindSpeed_kts_10min;
    public gustSpeedGroup10min: ParameterGroupInfo = ParameterGroupInfoSet.GustSpeed_kts_10min;

    constructor(public mainSite: MainSite, private ctx: ILiveDataListenerContext, private is10m: boolean = false) {
        this.windCompasses = [];

        if (this.is10m == true) {
            this.label += ' (10m)';
            this.id += '10m';

            this.windSpeedGroup = ParameterGroupInfoSet.WindSpeed_kts_10m;
            this.gustSpeedGroup = ParameterGroupInfoSet.GustSpeed_kts_10m;
            this.windSpeedGroup1min = ParameterGroupInfoSet.WindSpeed_kts_10m_1min;
            this.gustSpeedGroup1min = ParameterGroupInfoSet.GustSpeed_kts_10m_1min;
            this.windSpeedGroup10min = ParameterGroupInfoSet.WindSpeed_kts_10m_10min;
            this.gustSpeedGroup10min = ParameterGroupInfoSet.GustSpeed_kts_10m_10min;
        }
    }

    public notifyResize(): void {
        this.windCompasses.forEach((compass => compass.drawCompass()));
    }

    public render(container: JQuery, listeners: ILiveDataListener[], station: ParameterSchemaStation, warningGroup: WarningGroup): void {

        this.station = station;
        this.warningGroup = warningGroup;

        var page = $('<div class="midi_SiteObservations_padded_page_content"></div>');

        let chartSelectTabs = $('<div class="current_chart_selector"></div>')
            .appendTo(page);

        var windSensors = [];
        var combinedWindSensor = null;

        for (var sensorId of this.validSensorIds) {

            var sensor: ParameterSchemaSensor = this.mainSite.staticDataManager.Schema.ParameterSchema.Stations[station.Station.Id].Sensors[sensorId];

            if (sensor.SensorDefinition && sensor.SensorDefinition.Combined) {
                combinedWindSensor = sensorId;
            } else {
                windSensors.push(sensorId);
            }
        }

        windSensors.sort(); // sort alphabetically.

        if (combinedWindSensor) { // puch combined sensor to the start of the list
            windSensors.unshift(combinedWindSensor);
        }

        if (windSensors.length <= 4) {

            let tpul = $('<ul></ul>')
                .append($('<span></span>').text("Chart type: "))
                .appendTo(chartSelectTabs);         

            var topPanel = $('<div></div>')
                .css('position', 'absolute')
                .css('height', '25em')
                .css('width', '64em')
                .appendTo(page);

            //adding the time selector
            let allowedTimePeriods = [24, 168, 720];
            let timePeriodSelector = $('<div class="current_chart_selector"></div>')
                .css('margin-top','30px')
                .append($('<span></span>').text("Time period: "))
                .appendTo(topPanel);

            let timeSelector = $('<ul></ul>')
                .appendTo(timePeriodSelector);

            allowedTimePeriods.forEach((a) => {
                timeSelector.append($('<li></li>')
                    .text(DirectionalPolarChart.formatHoursToDays(a))
                    .attr('hours', a)
                    .click((ev) => {
                        timeSelector.children('li').removeClass('selected');
                        $(ev.target).addClass("selected");
                        this.selectedHours = parseInt($(ev.target).attr('hours'));
                        //update all charts for each sensor
                        for (var pch of this.polarCharts) {
                            pch.setSelectedHours(this.selectedHours);
                            pch.updateChart(this.selectedHours, true);
                        }
                    }));
            });

            let compassBox = $('<div></div>').appendTo(topPanel);
            let polarBox = $('<div></div>').appendTo(topPanel);

            tpul.append($('<li></li>')
                .text("Compass")
                .click((ev) => {
                    tpul.children('li').removeClass('selected');
                    $(ev.target).addClass("selected");
                    polarBox.hide();
                    timePeriodSelector.hide();
                    compassBox.show();
                    MainSite.instance.dispatchResize();
                }));

            tpul.append($('<li></li>')
                .text("Polar")
                .click((ev) => {
                    tpul.children('li').removeClass('selected');
                    $(ev.target).addClass("selected");
                    compassBox.hide();
                    timePeriodSelector.show();
                    timeSelector.children('li').first().click();
                    polarBox.show();
                    MainSite.instance.dispatchResize();
                }));

            tpul.children('li').first().click();

            const chartWidth = 25;
            const padding = (100 - (chartWidth * windSensors.length)) / (windSensors.length + 1);

            // adaptive compass tab & polar tab filled with charts

            console.info('Wind Tab - windSensors: ', windSensors, this);

            windSensors.forEach((sensorId, index) => {

                const left = (chartWidth + padding) * index + padding;
                this.renderCompassSensor(sensorId, listeners, compassBox, left, chartWidth);

                const pch = new DirectionalPolarChart(this.mainSite,
                    this,
                    polarBox,
                    15, left, 95, chartWidth,
                    this.windSpeedGroup.Id, ParameterGroupInfoSet.WindDirection_deg.Id,
                    false,
                    sensorId
                );
                this.polarCharts.push(pch)
                listeners.push(pch);
            });

            // bottom-chart
            var bottomPanel = $('<div></div>')
                .css('position', 'absolute')
                .css('bottom', 0)
                .css('height', '21em')
                .css('width', '64em')
                .appendTo(page);

            listeners.push(
                new WindChart(this.mainSite,
                    this,
                    windSensors,
                    bottomPanel,
                    0, 0, 100, 100)
            );
        } else {
            // table view only
            var leftPanel = $('<div></div>')
                .css('position', 'absolute')
                .css('height', '95%')
                .css('width', '100%')
                .appendTo(page);

            $('<div class="midi_SiteObservations_wind_sensor_table_header"></div>').appendTo(leftPanel);

            var height = 94 / windSensors.length;

            $('<div class="midi_SiteObservations_wind_sensor_table_header midi_SiteObservations_wind_sensor_table_header_top_row" style="top: 0%; left: 31%;">Dir (10 min)</div>').appendTo(leftPanel);
            $('<div class="midi_SiteObservations_wind_sensor_table_header midi_SiteObservations_wind_sensor_table_header_top_row" style="top: 0%; left: 54%;">Spd (10 min)</div>').appendTo(leftPanel);
            $('<div class="midi_SiteObservations_wind_sensor_table_header midi_SiteObservations_wind_sensor_table_header_top_row" style="top: 0%; left: 77%;">Gust (10 min)</div>').appendTo(leftPanel);

            windSensors.forEach((sensorId, index) => {

                var levelText = "";
                var sensor = MainSite.instance.staticDataManager.Schema.ParameterSchema.Stations[this.station.Station.Id].Sensors[sensorId];
                if (sensor.SensorDefinition && sensor.SensorDefinition.Level) {
                    levelText = '@ ' + sensor.SensorDefinition.Level;
                }

                var top = 6 + (height * index);

                $('<div class="midi_SiteObservations_wind_sensor_table_header midi_SiteObservations_wind_sensor_table_header_param"></div>')
                    .append($('<div>').text(sensorId).append('<br />').append($('<span>').text(levelText)))
                    .css('height', height + '%')
                    .css('top', top + '%')
                    .appendTo(leftPanel);

                listeners.push(
                    new ValueBox(this,
                        leftPanel,
                        new DefaultGroupFormatter(ParameterGroupInfoSet.WindDirection_deg_10min, sensorId),
                        top, 31, height, 23, this.ctx,
                        false, 2)
                );


                listeners.push(
                    new ValueBox(this,
                        leftPanel,
                        new DefaultGroupFormatter(this.windSpeedGroup10min, sensorId),
                        top, 54, height, 23, this.ctx,
                        false, 2)
                );

                listeners.push(
                    new ValueBox(this,
                        leftPanel,
                        new DefaultGroupFormatter(this.gustSpeedGroup10min, sensorId),
                        top, 77, height, 23, this.ctx,
                        false, 2)
                );

            });


        }


        page.appendTo(container);
    }

    private renderCompassSensor(sensorId: string, listeners: ILiveDataListener[], topPanel: JQuery, left: number, width: number) {

        // compass
        var windCompass = new WindCompass(this,
            topPanel,
            0, left, 53, width, // top, left, height, width, as %
            sensorId, this.is10m)

        listeners.push(windCompass);
        this.windCompasses.push(windCompass);

        // boxes
        var boxesContainer = $('<div></div>')
            .css('position', 'absolute')
            .css('height', '45%')
            .css('width', width + '%')
            .css('left', left + '%')
            .css('bottom', '0')
            .appendTo(topPanel);

        $('<div class="midi_SiteObservations_wind_sensor_header"></div>').appendTo(boxesContainer);

        $('<div class="midi_SiteObservations_wind_sensor_header midi_SiteObservations_wind_sensor_header_param" style="top: 13%;">Dir<br /><span>°T</span></div>').appendTo(boxesContainer);
        $(`<div class="midi_SiteObservations_wind_sensor_header midi_SiteObservations_wind_sensor_header_param" style="top: 42%;">Spd<br /><span>${MainSite.getUnitConverter().getConvertedUnits(this.windSpeedGroup10min.Units, this.windSpeedGroup10min.Id)}</span></div>`).appendTo(boxesContainer);
        $(`<div class="midi_SiteObservations_wind_sensor_header midi_SiteObservations_wind_sensor_header_param" style="top: 71%;">Gust<br /><span>${MainSite.getUnitConverter().getConvertedUnits(this.gustSpeedGroup.Units, this.gustSpeedGroup.Id)}</span></div>`).appendTo(boxesContainer);

        $('<div class="midi_SiteObservations_wind_sensor_header midi_SiteObservations_wind_sensor_header_top_row" style="top: 0%; left: 24%;">1 min</div>').appendTo(boxesContainer);
        $('<div class="midi_SiteObservations_wind_sensor_header midi_SiteObservations_wind_sensor_header_top_row" style="top: 0%; left: 62%;">10 min</div>').appendTo(boxesContainer);


        listeners.push(
            new ValueBox(this,
                boxesContainer,
                new DefaultGroupFormatter(ParameterGroupInfoSet.WindDirection_deg_1min, sensorId),
                13, 24, 29, 38, this.ctx,
                false, 2)
        );


        listeners.push(
            new ValueBox(this,
                boxesContainer,
                new DefaultGroupFormatter(this.windSpeedGroup1min, sensorId),
                42, 24, 29, 38, this.ctx,
                false, 2)
        );

        listeners.push(
            new ValueBox(this,
                boxesContainer,
                new DefaultGroupFormatter(this.gustSpeedGroup1min, sensorId),
                71, 24, 29, 38, this.ctx,
                false, 2)
        );

        listeners.push(
            new ValueBox(this,
                boxesContainer,
                new DefaultGroupFormatter(ParameterGroupInfoSet.WindDirection_deg_10min, sensorId),
                13, 62, 29, 38, this.ctx,
                false, 2)
        );


        listeners.push(
            new ValueBox(this,
                boxesContainer,
                new DefaultGroupFormatter(this.windSpeedGroup10min, sensorId),
                42, 62, 29, 38, this.ctx,
                false, 2)
        );

        listeners.push(
            new ValueBox(this,
                boxesContainer,
                new DefaultGroupFormatter(this.gustSpeedGroup10min, sensorId),
                71, 62, 29, 38, this.ctx,
                false, 2)
        );
    }
}